import React from 'react';
import styles from './CalendarHeader.module.scss';
import headerActionStyles from './components/HeaderActionButtons.module.scss';
import useWindowSize from '../../../../utils/useWindowSize';
import { SlidersHorizontal, Settings } from 'lucide-react';
import HeaderInject from '../../../Common/HeaderInject';
import SearchInput from '../../../Common/SearchInput';
import { useDispatch, useSelector } from 'react-redux';
import { getCalendarWorkers } from '../../store/selectors';
import DaySelector from './components/DaySelector';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import ExtraTimeSelector from './components/ExtraTimeSelector';
import CalendarCustomizeSelector from './components/CalendarCustomizeSelector';
import { modalActions } from '../../../Modals/store';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  COMPACT_CLOSED_DAYS_PARAM,
  HIDE_CLOSED_CHAIRS_PARAM,
  HIDE_TASK_ICONS_PARAM,
  HIDE_TASK_TIME_PARAM,
  isClosureFilterOn,
} from '../utils/closureHelpers';

const CalendarHeader = ({
  searchParams,
  setSearchParams: setSearchParamsValues,
  startDay,
  numberOfActiveDays,
  activeDays,
  searchString,
  setSearchString,
  activeWorker,
  setStepSize,
  stepSize,
  workTime,
  giorniVisibiliSalonePassato,
  onRefreshCalendar,
  canRefreshCalendar = true,
  refreshHighlighted = false,
  onRefreshHighlightClear,
}) => {
  const { isMobile, isTablet } = useWindowSize();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const daySelectedValues = [
    {
      name: `${t('scheduler.calendarHeader.day')}`,
      value: 1,
    },
    {
      name: `3 ${t('scheduler.calendarHeader.days')}`,
      value: 3,
    },
    {
      name: `7 ${t('scheduler.calendarHeader.days')}`,
      value: 7,
    },
    {
      name: `15 ${t('scheduler.calendarHeader.days')}`,
      value: 15,
    },
    {
      name: `30 ${t('scheduler.calendarHeader.days')}`,
      value: 30,
    },
  ];

  const setSearchParams = (nextParams) => {
    const params = new URLSearchParams(nextParams);
    localStorage.setItem('searchParams', JSON.stringify(Object.fromEntries(params)));
    setSearchParamsValues(params);
  };
  const calendarWorkers = useSelector(getCalendarWorkers());
  const selectorWorkers = [
    {
      value: 0,
      name: t('scheduler.calendarHeader.allChairs'),
    },
    ...calendarWorkers.map((worker) => {
      return { value: worker.value, name: worker.name };
    }),
  ];
  const daySettings = numberOfActiveDays === 1 && dayjs().isSameOrBefore(startDay, 'day');
  const canOpenSettings = !(localStorage.getItem('staff') && searchParams?.get('allowInsert') != 1);
  const hideClosedChairs = isClosureFilterOn(searchParams, HIDE_CLOSED_CHAIRS_PARAM);
  const compactClosedDays = isClosureFilterOn(searchParams, COMPACT_CLOSED_DAYS_PARAM);
  const showTaskIcons = !isClosureFilterOn(searchParams, HIDE_TASK_ICONS_PARAM);
  const showTaskTime = !isClosureFilterOn(searchParams, HIDE_TASK_TIME_PARAM);

  const setClosureParam = (key, enabled) => {
    const next = new URLSearchParams(searchParams);
    if (enabled) next.set(key, '1');
    else next.delete(key);
    setSearchParams(next);
  };

  const openCustomizeModal = () =>
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'calendarHeaderModal',
        props: {
          numberOfActiveDays,
          daySelectedValues,
          calendarWorkers: selectorWorkers,
          searchParams,
          setSearchParams,
          setStepSize,
          workTime,
          stepSize,
          workerId: searchParams.get('activeWorkerId') || activeWorker?.idpoltrona || 0,
        },
      }),
    );

  const openSettingsModal = () =>
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'calendarSettingsModal',
        props: {
          startDay,
          daySettings,
          searchParams,
        },
      }),
    );

  return (
    <HeaderInject className={isMobile && 'calendar'}>
      {isMobile ? (
        <div className={styles.header_container_mobile}>
          <div className={styles.header_container__search}>
            <SearchInput
              placeholder={t('scheduler.calendarHeader.search')}
              value={searchString}
              setValue={setSearchString}
              fullWidth
              compact
            />
          </div>
          <div className={styles.date_container}>
            <Button
              disableElevation
              variant="contained"
              className={styles.calendar_today_button}
              onClick={() => {
                searchParams.set('startDay', dayjs().format('YYYY-MM-DD'));
                setSearchParams(searchParams);
              }}
            >
              {t('scheduler.calendarHeader.today')}
            </Button>
            <DaySelector
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              startDay={startDay}
              numberOfActiveDays={numberOfActiveDays}
              activeDays={activeDays}
              giorniVisibiliSalonePassato={giorniVisibiliSalonePassato}
              onRefreshCalendar={onRefreshCalendar}
              canRefreshCalendar={canRefreshCalendar}
              refreshHighlighted={refreshHighlighted}
              onRefreshHighlightClear={onRefreshHighlightClear}
            />
          </div>
          <div
            className={clx(
              headerActionStyles.header_actions,
              headerActionStyles.header_actions_mobile,
              styles.header_actions_mobile_slot,
            )}
          >
            <button
              type="button"
              className={clx(
                headerActionStyles.action_button,
                headerActionStyles.action_button_mobile,
                headerActionStyles.action_button_icon_only,
              )}
              onClick={openCustomizeModal}
              title={t('scheduler.calendarHeader.filters')}
              aria-label={t('scheduler.calendarHeader.filters')}
            >
              <SlidersHorizontal size={20} strokeWidth={2} />
            </button>
            {canOpenSettings ? (
              <button
                type="button"
                className={clx(
                  headerActionStyles.action_button,
                  headerActionStyles.action_button_mobile,
                  headerActionStyles.action_button_icon_only,
                )}
                onClick={openSettingsModal}
                title={t('scheduler.calendarHeader.extra')}
                aria-label={t('scheduler.calendarHeader.extra')}
              >
                <Settings size={20} strokeWidth={2} />
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={styles.header_container}>
          <div className={styles.header_container__item}>
            <Button
              disableElevation
              variant="contained"
              className={styles.calendar_today_button}
              onClick={() => {
                if (searchParams.get('startDay') != dayjs().format('YYYY-MM-DD'))
                  searchParams.set('reset', 1);
                else searchParams.set('reset', 2);

                searchParams.set('startDay', dayjs().format('YYYY-MM-DD'));

                setSearchParams(searchParams);
              }}
            >
              {t('scheduler.calendarHeader.today')}
            </Button>
            <DaySelector
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              startDay={startDay}
              numberOfActiveDays={numberOfActiveDays}
              activeDays={activeDays}
              giorniVisibiliSalonePassato={giorniVisibiliSalonePassato}
            />
          </div>
          <div className={styles.header_container__search}>
            <SearchInput
              placeholder={t('scheduler.calendarHeader.searchClientService')}
              value={searchString}
              setValue={setSearchString}
            />
          </div>
          <div className={styles.header_container__item}>
            <div className={headerActionStyles.header_actions}>
              {isTablet ? (
                <>
                  <button
                    type="button"
                    className={headerActionStyles.action_button}
                    onClick={openCustomizeModal}
                    title={t('scheduler.calendarHeader.filters')}
                    aria-label={t('scheduler.calendarHeader.filters')}
                  >
                    <SlidersHorizontal size={18} strokeWidth={2} />
                    <span>{t('scheduler.calendarHeader.filters')}</span>
                  </button>
                  {canOpenSettings ? (
                    <button
                      type="button"
                      className={headerActionStyles.action_button}
                      onClick={openSettingsModal}
                      title={t('scheduler.calendarHeader.extra')}
                      aria-label={t('scheduler.calendarHeader.extra')}
                    >
                      <Settings size={18} strokeWidth={2} />
                      <span>{t('scheduler.calendarHeader.extra')}</span>
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <CalendarCustomizeSelector
                    title={t('scheduler.calendarHeader.filters')}
                    calendarWorkers={selectorWorkers}
                    activeWorkerId={activeWorker?.idpoltrona || searchParams.get('activeWorkerId') || 0}
                    onActiveWorkerChange={(activeWorkerId) => {
                      const next = new URLSearchParams(searchParams);
                      next.set('activeWorkerId', String(activeWorkerId ?? 0));
                      setSearchParams(next);
                    }}
                    daySelectedValues={daySelectedValues}
                    numberOfActiveDays={numberOfActiveDays}
                    onNumberOfActiveDaysChange={(days) => {
                      const next = new URLSearchParams(searchParams);
                      next.set('numberOfActiveDays', String(days));
                      setSearchParams(next);
                    }}
                    stepSize={stepSize}
                    onStepSizeChange={setStepSize}
                    workTime={workTime}
                    searchParams={searchParams}
                    hideClosedChairs={hideClosedChairs}
                    onHideClosedChairsChange={(enabled) =>
                      setClosureParam(HIDE_CLOSED_CHAIRS_PARAM, enabled)
                    }
                    compactClosedDays={compactClosedDays}
                    onCompactClosedDaysChange={(enabled) =>
                      setClosureParam(COMPACT_CLOSED_DAYS_PARAM, enabled)
                    }
                    showTaskIcons={showTaskIcons}
                    onShowTaskIconsChange={(enabled) =>
                      setClosureParam(HIDE_TASK_ICONS_PARAM, !enabled)
                    }
                    showTaskTime={showTaskTime}
                    onShowTaskTimeChange={(enabled) =>
                      setClosureParam(HIDE_TASK_TIME_PARAM, !enabled)
                    }
                  />
                  {canOpenSettings ? (
                    <ExtraTimeSelector
                      daySettings={daySettings}
                      title={t('scheduler.calendarHeader.extra')}
                      startDay={startDay}
                      searchParams={searchParams}
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </HeaderInject>
  );
};

export default CalendarHeader;
